<template>
    <UiModal :is-open="showPrompt" @close="handleRemindLater">
        <template #title>
            <div class="flex items-center">
                <div
                    class="w-12 h-12 rounded-full flex flex-col justify-center items-center mr-3 bg-slate-100 dark:bg-slate-700"
                >
                    <svg
                        class="w-6 h-6 text-slate-600 dark:text-slate-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                    </svg>
                </div>
                <div>
                    <h5 class="ui-modal-title">
                        {{
                            currentStep === 'intro'
                                ? 'Faster Login with Passkeys'
                                : 'Add New Passkey'
                        }}
                    </h5>
                    <p class="text-slate-500 dark:text-slate-400 text-sm">
                        {{
                            currentStep === 'intro'
                                ? 'Sign in instantly with Face ID, Touch ID, or Windows Hello instead of typing your password.'
                                : "Give your passkey a name to help you remember which device it's for."
                        }}
                    </p>
                </div>
            </div>
        </template>

        <template #content>
            <!-- Step 1: Introduction with benefits -->
            <div
                v-if="currentStep === 'intro'"
                class="space-y-3 text-sm text-slate-600 dark:text-slate-300"
            >
                <div class="flex items-start gap-3">
                    <svg
                        class="w-5 h-5 text-green-600 mt-0.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <span>More secure than passwords</span>
                </div>
                <div class="flex items-start gap-3">
                    <svg
                        class="w-5 h-5 text-green-600 mt-0.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <span>No passwords to remember</span>
                </div>
                <div class="flex items-start gap-3">
                    <svg
                        class="w-5 h-5 text-green-600 mt-0.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <span>Works across your devices</span>
                </div>
            </div>

            <!-- Step 2: Name input -->
            <div v-else-if="currentStep === 'name'">
                <input
                    v-model="passkeyName"
                    type="text"
                    placeholder="e.g., iPhone, MacBook Pro"
                    :class="[
                        'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent',
                        validationError
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-slate-300 dark:border-slate-600 focus:ring-slate-400',
                    ]"
                    @keyup.enter="handleCreatePasskey"
                    ref="nameInput"
                />
                <p v-if="validationError" class="mt-2 text-sm text-red-600">
                    {{ validationError }}
                </p>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between items-center w-full">
                <button
                    v-if="currentStep === 'intro'"
                    @click="handleNeverAskAgain"
                    class="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                >
                    Don't ask again
                </button>
                <div v-else></div>

                <div class="flex gap-4">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="
                            currentStep === 'intro'
                                ? handleRemindLater()
                                : (currentStep = 'intro')
                        "
                    >
                        {{
                            currentStep === 'intro'
                                ? 'Remind Me Later'
                                : 'Cancel'
                        }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="
                            currentStep === 'intro'
                                ? (currentStep = 'name')
                                : handleCreatePasskey()
                        "
                        :disabled="
                            loading ||
                            (currentStep === 'name' && !passkeyName.trim())
                        "
                    >
                        {{
                            currentStep === 'intro'
                                ? 'Set Up Passkey Now'
                                : 'Create Passkey'
                        }}
                        <span class="inline ml-2" v-if="loading">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLoginStore } from '@/store/login';
import { useAppStore } from '@/store/app';
import { usePasskey } from '@/composables/usePasskey';
import { usePage } from '@inertiajs/vue3';
import { Apii } from '@/services/api.js';
import { notify } from 'notiwind';
import UiModal from '@/components/ui/modal/ui-modal.vue';

const ApiService = new Apii();
const loginStore = useLoginStore();
const appStore = useAppStore();
const { activePopup } = storeToRefs(appStore);
const { register, loading, error } = usePasskey();
const page = usePage();
const userData = computed(() => page.props.userData || {});
const showPrompt = ref(false);
const pendingPrompt = ref(false);
const currentStep = ref('intro'); // 'intro' or 'name'
const passkeyName = ref('');
const nameInput = ref(null);
const validationError = ref('');
const existingPasskeys = ref([]);
const hasCheckedOnce = ref(false);

onMounted(async () => {
    // Show prompt if user doesn't have passkeys and should be prompted
    // Only check once per session to avoid showing on every page load
    if (!hasCheckedOnce.value && !userData.value.hasPasskey) {
        hasCheckedOnce.value = true;

        // Check if user should be prompted based on their preferences
        const shouldPrompt = await checkIfShouldPrompt();
        if (shouldPrompt) {
            if (activePopup.value === null) {
                showPrompt.value = true;
            } else {
                // Another popup is showing — defer until it's dismissed
                pendingPrompt.value = true;
            }
        }
    }

    // Load existing passkeys for duplicate checking
    try {
        const response = await ApiService.apiCall('/user/passkeys', 'get');
        existingPasskeys.value = response.data.passkeys || [];
    } catch (err) {
        console.error('Failed to load passkeys:', err);
    }
});

// Show passkey prompt once all higher-priority popups have been dismissed
watch(activePopup, (val) => {
    if (val === null && pendingPrompt.value) {
        pendingPrompt.value = false;
        showPrompt.value = true;
    }
});

const checkIfShouldPrompt = async () => {
    try {
        const response = await ApiService.apiCall(
            '/user/passkey-preferences/should-prompt',
            'get'
        );
        return response.data.should_prompt || false;
    } catch (err) {
        console.error('Failed to check passkey preferences:', err);
        return false;
    }
};

// Focus input when switching to name step
watch(currentStep, async (newStep) => {
    if (newStep === 'name') {
        await nextTick();
        nameInput.value?.focus();
    }
});

// Check for duplicate names as user types
watch(passkeyName, (newName) => {
    if (!newName.trim()) {
        validationError.value = '';
        return;
    }

    const isDuplicate = existingPasskeys.value.some(
        (passkey) => passkey.name.toLowerCase() === newName.trim().toLowerCase()
    );

    if (isDuplicate) {
        validationError.value =
            'You already have a passkey with this name. Please choose a different name.';
    } else {
        validationError.value = '';
    }
});

// Reset state when modal closes
watch(showPrompt, (isOpen) => {
    if (!isOpen) {
        currentStep.value = 'intro';
        passkeyName.value = '';
        validationError.value = '';
    }
});

const handleCreatePasskey = async () => {
    if (!passkeyName.value.trim()) {
        return;
    }

    validationError.value = '';

    try {
        // Register passkey with user-provided name
        await register(passkeyName.value.trim());

        notify(
            {
                group: 'success',
                title: 'Passkey Added!',
                text: 'You can now use your passkey to sign in.',
            },
            4000
        );

        showPrompt.value = false;
    } catch (err) {
        // Check if it's a validation error (422)
        if (err.response?.status === 422 && err.response?.data?.errors?.name) {
            validationError.value = err.response.data.errors.name[0];
        } else {
            notify(
                {
                    group: 'error',
                    title: 'Setup Failed',
                    text:
                        error.value ||
                        'Failed to set up passkey. You can try again from Settings.',
                },
                4000
            );
        }
    }
};

const handleRemindLater = async () => {
    try {
        await ApiService.apiCall(
            '/user/passkey-preferences/remind-later',
            'post'
        );
        showPrompt.value = false;

        notify(
            {
                group: 'success',
                title: 'Reminder Set',
                text: "We'll remind you about passkeys in 7 days.",
            },
            4000
        );
    } catch (err) {
        console.error('[PasskeySetupPrompt] Failed to save preference:', err);
        showPrompt.value = false;
    }
};

const handleNeverAskAgain = async () => {
    try {
        await ApiService.apiCall(
            '/user/passkey-preferences/never-prompt',
            'post'
        );
        showPrompt.value = false;

        notify(
            {
                group: 'success',
                title: 'Preference Saved',
                text: 'You can still set up passkeys anytime from Settings.',
            },
            4000
        );
    } catch (err) {
        console.error('Failed to save preference:', err);
        showPrompt.value = false;
    }
};
</script>
